// UCC tab — shared lapse-countdown derivations. Severity drives the
// "loudest column" treatment on the filings table and the header tiles:
//   • error:   lapsed / past lapse date, or ≤ 30 days out
//   • warning: inside the continuation window (server window when present,
//              §9-515 180-day fallback otherwise)
//   • none:    comfortably out, or terminal statuses where a countdown
//              would be noise.
//
// The backend writes lowercase statuses ("active" / "continued" /
// "terminated" / "lapsed" — UccFilingService + UccLapseDetectionWorker);
// every comparison normalizes case like the insurance policyStatus helpers.

import { daysUntil } from "../common/colorBand";
import type { UccFilingDto } from "@/api/portfolioAdmin/types";

export type LapseSeverity = "error" | "warning" | "none";

const TERMINAL_STATUSES = new Set(["terminated"]);

export function normalizeUccStatus(status: string | null | undefined): string {
  return (status ?? "").trim().toLowerCase();
}

/** Is the §9-515 continuation window open right now for this filing?
 *  Prefers the server-computed window; falls back to lapse − 180 days. */
export function continuationWindowOpenNow(filing: UccFilingDto): boolean {
  const lapse = new Date(filing.lapseDate).getTime();
  if (Number.isNaN(lapse)) return false;
  const open = filing.continuationWindowOpen
    ? new Date(filing.continuationWindowOpen).getTime()
    : lapse - 180 * 86_400_000;
  const close = filing.continuationWindowClose
    ? new Date(filing.continuationWindowClose).getTime()
    : lapse;
  if (Number.isNaN(open) || Number.isNaN(close)) return false;
  const now = Date.now();
  return now >= open && now <= close;
}

/**
 * `tz` is the VIEWER's zone, threaded from the component that renders the
 * row. This module is pure and cannot read React context, so the parameter is
 * the only way the countdown behind the severity and the countdown printed
 * beside it can agree — `ActiveFilingsTable` renders both in one `<TD>`, and
 * a severity computed in Phoenix beside a label computed in the viewer's zone
 * is a cell whose colour contradicts its own text.
 */
export function lapseSeverity(
  filing: UccFilingDto,
): LapseSeverity {
  if (TERMINAL_STATUSES.has(normalizeUccStatus(filing.currentStatus))) return "none";
  const days = daysUntil(filing.lapseDate);
  if (days === null) return "none";
  if (days <= 30) return "error";
  if (continuationWindowOpenNow(filing)) return "warning";
  return "none";
}

export function isLapsed(filing: UccFilingDto): boolean {
  const status = normalizeUccStatus(filing.currentStatus);
  return status === "lapsed" || status === "expired";
}

export function isActive(filing: UccFilingDto): boolean {
  return normalizeUccStatus(filing.currentStatus) === "active";
}
